import axiosLib, { AxiosError, AxiosRequestConfig } from "axios";
import { refreshToken } from "~/api/account/requests";
import { MaxRetriesError } from "~/helpers/errors";
import {
  getJWT,
  setJWT,
  getRefreshToken,
  setRefreshToken,
} from "~/helpers/auth";
import { paths } from "~/router/paths";
import { guestPaths } from "~/constants/paths";
import { endpointsToExcludeTheJWT } from "~/api/constants";
import { getNow, trackApiRequest } from "~/helpers/datadog";
import { noteFailure, noteSuccess } from "~/helpers/maintenance";

const apiURL: string = import.meta.env.VITE_API_URL;

export const axios = axiosLib.create({
  timeout: 60000,
});

const getURL = (path: string) =>
  `${apiURL}/${path.endsWith("/") ? path : path + "/"}`;

type DatadogAxiosConfig = AxiosRequestConfig & {
  metadata?: {
    requestStartTime: number;
  };
};

const getRequestDuration = (config?: AxiosRequestConfig) => {
  const requestStartTime = (config as DatadogAxiosConfig | undefined)?.metadata
    ?.requestStartTime;

  if (!requestStartTime) return 0;
  return getNow() - requestStartTime;
};

// Sign-in, registration, password reset and refresh carry no token, so a 401
// from them is the answer itself (wrong password, dead refresh token) and
// must reach the caller unchanged.
const isJWTExcludedEndpoint = (url?: string): boolean =>
  !!url && endpointsToExcludeTheJWT.some((endpoint) => url.includes(endpoint));

const handleRequest = (config: AxiosRequestConfig): any => {
  (config as DatadogAxiosConfig).metadata = { requestStartTime: getNow() };
  const isAuthEndpoint = isJWTExcludedEndpoint(config.url);
  const isS3Endpoint = (() => {
    if (!config.url) return false;
    try {
      const { hostname } = new URL(config.url);
      return (
        hostname === "s3.amazonaws.com" ||
        hostname.endsWith(".s3.amazonaws.com") ||
        (hostname.startsWith("s3.") && hostname.endsWith(".amazonaws.com"))
      );
    } catch {
      return false;
    }
  })();

  if (isS3Endpoint || isAuthEndpoint) {
    return config;
  }

  const jwt = getJWT();
  if (jwt && config.headers) config.headers.Authorization = `Bearer ${jwt}`;
  return config;
};

axios.interceptors.request.use(handleRequest);

axios.interceptors.response.use(
  (response) => {
    trackApiRequest({
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      durationMs: getRequestDuration(response.config),
    });

    // A live success is the strongest evidence the API is up — clears any
    // in-progress outage bookkeeping. See ~/helpers/maintenance.
    noteSuccess();

    return response;
  },
  (error: AxiosError) => {
    trackApiRequest({
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status,
      durationMs: getRequestDuration(error.config),
      errorMessage: error.message,
    });

    // error.response is undefined for an opaque infrastructure-level failure
    // (nginx/ALB 5xx, dead connection) — exactly the outage shape this is
    // meant to catch. Fire-and-forget: never throws, never touches the retry/
    // sign-out branches below, which are gated on err.response separately.
    void noteFailure(error.response?.status, error.config?.url);

    return Promise.reject(error);
  }
);

/** Returns true when it started the redirect to login. */
export const forceSignOut = (): boolean => {
  const { pathname, search } = window.location;
  if (guestPaths.find((path) => new RegExp(path).test(pathname))) {
    return false; // Prevent sign out on pages where the user is not logged in.
  }

  setJWT("");
  setRefreshToken("");
  // Send the user back to this page once they have logged in again.
  const next = encodeURIComponent(pathname + search);
  window.location.href = `${paths.auth.login}?next=${next}`;
  return true;
};

// Once the redirect to login has started, the failed request never settles:
// resolving with nothing makes callers reject with "No data" and log
// "Failed to fetch account" while the page is unloading. Guest pages keep
// resolving with nothing, as before.
const signOutAndHalt = (): Promise<never> | undefined =>
  forceSignOut() ? new Promise<never>(() => {}) : undefined;

const handleErrorWrapper = async <T = any>(
  next: Function,
  retries: number = 5
): Promise<T | void> => {
  if (retries <= 0) {
    throw new MaxRetriesError("Max retries reached");
  }

  try {
    return await next();
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response) {
        const { status } = err.response;
        const refresh = getRefreshToken();

        if (status === 402) {
          localStorage.setItem("plansModal", "true");
        }

        if (status === 401) {
          if (refresh) {
            if (err?.config?.url?.includes("/token/refresh/")) {
              return signOutAndHalt();
            }

            // A rejected refresh resolves with no data (its own 401 is handled
            // by this wrapper), so check for a token rather than for a throw.
            // Saving a missing token would store "undefined" and retry with it
            // until MaxRetriesError (Error Tracking 7bea300a).
            let access: unknown;
            try {
              const res = await refreshToken({ refresh });
              access = res?.data?.access;
            } catch {
              // Only a network error, timeout or 5xx reaches here. The session
              // may still be valid, so fail this request instead of signing out.
              return Promise.reject(err);
            }
            if (typeof access !== "string" || !access) {
              return signOutAndHalt();
            }
            setJWT(access);

            return handleErrorWrapper(next, retries - 1);
          } else if (!isJWTExcludedEndpoint(err.config?.url)) {
            // No refresh token to try: the session is over, whichever
            // endpoint answered. (A bare "/auth/" check used to exempt
            // auth/user/ here, so a dead session kept polling instead of
            // leaving.)
            return signOutAndHalt();
          }
        }
      }
    }
    return Promise.reject(err);
  }
};

export const sendGet = async (path: string, params?: Object): Promise<any> => {
  return handleErrorWrapper(async () => {
    return axios.get(getURL(path), { params });
  });
};

export const sendPost = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.post(getURL(path), params);
  });
};

export const sendPut = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.put(getURL(path), params);
  });
};

export const sendDelete = async (path: string, params: Object) => {
  return handleErrorWrapper(async () => {
    return axios.delete(getURL(path), params);
  });
};
